import { Disclosure } from './Disclosure'
import { CoiSpeed, NO_INFORMATION } from './quoteCardTypes'
import { QuoteCardSection } from './quotesAnalytics'

const COI_SPEED_COPY: Record<CoiSpeed, string> = {
  [CoiSpeed.UNDER_24_HOURS]:
    'This carrier typically issues certificates of insurance in less than 24 hours, fast enough to accept most loads without delay.',
  [CoiSpeed.ONE_DAY_OR_MORE]:
    'This carrier typically takes a day or more to issue certificates of insurance. Plan ahead if a shipper needs one quickly.',
}

// Needle points to the middle of the amber (slower) or green (faster) segment.
// No carrier in this data set ever lands on the worst (red) segment.
const NEEDLE_ANGLE: Record<CoiSpeed, number> = {
  [CoiSpeed.ONE_DAY_OR_MORE]: 90,
  [CoiSpeed.UNDER_24_HOURS]: 150,
}

const CX = 100
const CY = 90
const RADIUS = 76
const NEEDLE_LENGTH = 66

function polarToCartesian(angle: number, radius: number) {
  const angleInRadians = ((angle - 180) * Math.PI) / 180
  return { x: CX + radius * Math.cos(angleInRadians), y: CY + radius * Math.sin(angleInRadians) }
}

/** Arc sweeping left (0°, worst) over the top (90°) to right (180°, best). */
function describeArc(startAngle: number, endAngle: number) {
  const start = polarToCartesian(endAngle, RADIUS)
  const end = polarToCartesian(startAngle, RADIUS)
  return `M ${start.x} ${start.y} A ${RADIUS} ${RADIUS} 0 0 0 ${end.x} ${end.y}`
}

interface CoiSpeedSectionProps {
  /** Unset when we have no sourced COI speed for the carrier. */
  coiSpeed?: CoiSpeed
}

/** Three-segment COI-speed gauge (worst/mid/best) plus a plain-language explanation. */
export function CoiSpeedSection({ coiSpeed }: CoiSpeedSectionProps) {
  if (!coiSpeed)
    return <Disclosure section={QuoteCardSection.COI_SPEED} label="COI speed" summary={NO_INFORMATION} />

  const needle = polarToCartesian(NEEDLE_ANGLE[coiSpeed], NEEDLE_LENGTH)

  return (
    <Disclosure section={QuoteCardSection.COI_SPEED} label="COI speed">
      <div className="flex flex-col gap-3">
        {/* w-28 is the Claims capabilities label column, so both drawers share a left column. */}
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 200 105" className="w-28 shrink-0">
            <path
              d={describeArc(0, 60)}
              className="stroke-error"
              fill="none"
              strokeWidth={14}
              strokeLinecap="round"
            />
            <path
              d={describeArc(60, 120)}
              className="stroke-(--amber)"
              fill="none"
              strokeWidth={14}
              strokeLinecap="round"
            />
            <path
              d={describeArc(120, 180)}
              className="stroke-accent-green"
              fill="none"
              strokeWidth={14}
              strokeLinecap="round"
            />
            <line
              x1={CX}
              y1={CY}
              x2={needle.x}
              y2={needle.y}
              className="stroke-ink-900"
              strokeWidth={3}
              strokeLinecap="round"
            />
            <circle cx={CX} cy={CY} r={5} className="fill-ink-900" />
          </svg>
          <p className="text-ink-700 text-xs leading-relaxed">{COI_SPEED_COPY[coiSpeed]}</p>
        </div>
        <p className="text-ink-400 text-[10px] leading-relaxed">
          COI speed is estimated by Cai based on past requests to this carrier. It may not reflect this
          carrier&apos;s current response time.
        </p>
      </div>
    </Disclosure>
  )
}
